/* global FB */
/* eslint-disable no-undef */

import React, { Component, lazy, Suspense } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import pt from 'prop-types'
import { connect } from 'react-redux'
import { get } from 'lodash-es'
import axios from 'axios'

import API from '../../../src/config'

import { setUserFB, requestLogout } from '../../modules/user'
import { openMod } from '../../modules/mods'
import { makeGetFullUserByUsername } from '../../modules/entities'

import Header from '../../components/Header'
import Footer from '../../components/Footer'
import RoutesContainer from '../../components/RoutesContainer/RoutesContainer'
import './PageLayout.scss'

const VideoContainer = lazy(() => import('../../components/VideoContainer'))
const AudioContainer = lazy(() => import('../../components/AudioContainer'))
const AsyncSearch = lazy(() => import('../../pages/web/Search/Search'))
const AsyncContact = lazy(() => import('../../pages/web/Contact/Contact'))
const AsyncLegal = lazy(() => import('../../pages/web/Legal/Legal'))
const AsyncNotFound = lazy(() => import('../../pages/web/NotFound'))
const AsyncProfile = lazy(() => import('../../pages/web/Profile/Profile'))
const AsyncPick = lazy(() => import('../../pages/web/Pick/Pick'))
const AsyncCollection = lazy(() => import('../../pages/web/Collection/Collection'))
const AsyncModal = lazy(() => import('../../components/modal/Modal'))
const AsyncSubmodal = lazy(() => import('../../components/modal/Submodal'))
const AsyncError = lazy(() => import('../../components/modal/ErrorMessage'))
const AsyncActivities = lazy(() => import('../../pages/web/Activities/Activities'))
const AsyncReadMode = lazy(() => import('../../pages/web/ReadMode'))
const AsyncListenMode = lazy(() => import('../../pages/web/ListenMode'))
const AsyncWatchMode = lazy(() => import('../../pages/web/WatchMode'))

class PageLayout extends Component {
  constructor() {
    super()
    axios.interceptors.response.use(
      function(response) {
        return response
      },
      error => {
        if (error && error.response && error.response.status === 401) {
          this.props.requestLogout()
          return this.props.openMod({ type: 'signup' })
        } else {
          return Promise.reject(error)
        }
      }
    )
  }

  componentDidMount() {
    const { setUserFB } = this.props

    let oneSignalOptions = {}
    if (location.href.includes('localhost')) {
      oneSignalOptions.appId = 'd7db7817-97bc-4c23-825f-f19e406c82d1'
      oneSignalOptions.subdomainName = 'guidehero-dev'
      oneSignalOptions.httpPermissionRequest = {
        enable: true
      }
    } else {
      oneSignalOptions = API.oneSignalOptions
    }

    window.scrollTo(0, 0)

    FB.init({
      appId: API.appId, // Set YOUR APP ID
      status: true, // check login status
      cookie: true, // enable cookies to allow the server to access the session
      xfbml: true // parse XFBML
    })

    FB.Event.subscribe('auth.authResponseChange', response => {
      setUserFB(response)
    })

    FB.getLoginStatus(response => {
      setUserFB(response)
    })

    if (get(navigator, 'serviceWorker.register', false)) {
      navigator.serviceWorker.register('/OneSignalSDKWorker.js')
    }

    const OneSignal = window.OneSignal || []

    if (!OneSignal.initialized) {
      OneSignal.push([
        'init',
        {
          autoRegister: false,
          notifyButton: {
            enable: false
          },
          ...oneSignalOptions
        }
      ])
    }
  }

  shouldComponentUpdate(nextProps) {
    const {
      location,
      mods: { list }
    } = this.props
    const {
      location: nextLocation,
      mods: { list: nextList }
    } = nextProps
    return nextLocation.pathname !== location.pathname || nextList.length !== list.length
  }

  render() {
    const { mods, history, match, user } = this.props
    return (
      <div className="page-layout">
        <Suspense fallback={<div className="page-layout__suspense">{/* <SkeletonGrid /> */}</div>}>
          <RoutesContainer>
            <Switch location={mods.list.length > 0 ? { pathname: mods.startLocation } : history.location}>
              <Redirect from="/profile" to={`/@${user.username}`} />
              <Route exact path={`/feed/:feedType`} component={AsyncSearch} />
              <Route path={`/picks/:cardId/read`} component={AsyncReadMode} />
              <Route exact path={`/picks/:cardId/listen`} component={AsyncListenMode} />
              <Route exact path={`/picks/:cardId/watch`} component={AsyncWatchMode} />
              <Route exact path={`/picks/:cardId`} component={AsyncPick} />
              <Route exact path={`/picks/:cardId/:action`} component={AsyncPick} />
              <Route exact path={`/collections/:collectionId`} component={AsyncCollection} />
              <Route exact path={`/collections/:collectionId/:action`} component={AsyncCollection} />
              <Route path={`/@:username`} component={AsyncProfile} />
              <Route exact path={`/privacy`} component={AsyncLegal} />
              <Route exact path={`/tos`} component={AsyncLegal} />
              <Route exact path={`/contact`} component={AsyncContact} />
              <Route path={`/search`} component={AsyncSearch} />
              <Route exact path="/" render={props => <AsyncSearch {...props} user={user} />} />
              <Route exact path="/home" component={AsyncSearch} />
              <Route exact path="/saved/:username" component={AsyncSearch} />
              <Route path={`/custom-feed/:feedId`} component={AsyncSearch} />
              <Route path={`/feed/top-comments`} component={AsyncSearch} />
              <Route exact path={'/activities'} component={AsyncActivities} />
              <Route path="*" component={AsyncNotFound} />
            </Switch>
          </RoutesContainer>
          <Header match={match} />
        </Suspense>
        <Footer />
        <Suspense fallback={null}>
          <AsyncModal history={history} />
        </Suspense>
        <Suspense fallback={null}>
          <AsyncSubmodal />
        </Suspense>
        <Suspense fallback={null}>
          <AsyncError />
        </Suspense>
        <Suspense fallback={null}>
          <VideoContainer />
        </Suspense>
        <Suspense fallback={null}>
          <AudioContainer />
        </Suspense>
      </div>
    )
  }
}

PageLayout.propTypes = {
  setUserFB: pt.func.isRequired,
  openMod: pt.func,
  requestLogout: pt.func,
  match: pt.shape({}),
  mods: pt.shape({
    list: pt.array,
    sublist: pt.array
  }),
  history: pt.shape({}),
  user: pt.shape({}),
  location: pt.object
}

const makeMapStateToProps = () => {
  const getCurrentUser = makeGetFullUserByUsername()
  return (state, props) => ({
    user: getCurrentUser(state),
    mods: state.mods,
    location: state.location
  })
}

export default connect(
  makeMapStateToProps,
  { setUserFB, openMod, requestLogout }
)(PageLayout)
